import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { useImportanceColors } from "@/hooks/useImportanceColors";
import { useUserPreferences } from "@/hooks/useUserPreferences";
import { toast } from "sonner";

type ColorValues = {
  [key: number]: {
    bg: string;
    text: string;
  };
};

export function ImportanceColorSettings() {
  const { t } = useTranslation();
  const { colorSettings, defaultColors, isDefaultColor } = useImportanceColors();
  const { updatePreferences } = useUserPreferences();
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [colorValues, setColorValues] = useState<ColorValues>({
    1: { ...colorSettings[1] },
    2: { ...colorSettings[2] },
    3: { ...colorSettings[3] },
    4: { ...colorSettings[4] },
    5: { ...colorSettings[5] },
  });

  // Update local state when colorSettings from the hook changes
  useEffect(() => {
    setColorValues({
      1: { ...colorSettings[1] },
      2: { ...colorSettings[2] },
      3: { ...colorSettings[3] },
      4: { ...colorSettings[4] },
      5: { ...colorSettings[5] },
    });
  }, [colorSettings]);

  const importanceLabels = {
    1: "Very Low",
    2: "Low",
    3: "Medium",
    4: "High",
    5: "Critical",
  };

  // Helper function to extract hex color from Tailwind class
  const extractColorFromClass = (colorClass: string): string => {
    if (!colorClass) return "#ffffff";
    
    // Handle direct hex values in the format bg-[#hex] or text-[#hex]
    const hexMatch = colorClass.match(/\[(#[A-Fa-f0-9]{3,6})\]/);
    if (hexMatch && hexMatch[1]) {
      return hexMatch[1];
    }
    
    // For default Tailwind classes that don't use custom hex values
    // This is a simplification - in a real app you might need a more comprehensive mapping
    if (colorClass.includes('bg-slate-200')) return "#e2e8f0";
    if (colorClass.includes('bg-blue-200')) return "#bfdbfe";
    if (colorClass.includes('bg-green-200')) return "#bbf7d0";
    if (colorClass.includes('bg-amber-200')) return "#fde68a";
    if (colorClass.includes('bg-red-200')) return "#fecaca";
    if (colorClass.includes('text-slate-700')) return "#334155";
    if (colorClass.includes('text-blue-700')) return "#1d4ed8";
    if (colorClass.includes('text-green-700')) return "#15803d";
    if (colorClass.includes('text-amber-700')) return "#b45309";
    if (colorClass.includes('text-red-700')) return "#b91c1c";
    
    // Default fallback colors if we can't extract
    return colorClass.includes('bg-') ? "#cbd5e1" : "#334155";
  };

  // Helper function to convert hex to tailwind class
  const hexToTailwindClass = (hex: string, type: 'bg' | 'text'): string => {
    return `${type}-[${hex}]`;
  };

  const handleColorChange = (importance: number, type: 'bg' | 'text', value: string) => {
    setColorValues(prev => ({
      ...prev,
      [importance]: {
        ...prev[importance],
        [type]: hexToTailwindClass(value, type)
      }
    }));
  };

  const handleReset = (importance: number) => {
    setColorValues(prev => ({
      ...prev,
      [importance]: { ...defaultColors[importance] }
    }));
  };

  const handleResetAll = () => {
    setColorValues({
      1: { ...defaultColors[1] },
      2: { ...defaultColors[2] },
      3: { ...defaultColors[3] },
      4: { ...defaultColors[4] },
      5: { ...defaultColors[5] },
    });
  };

  const handleSave = async () => {
    setIsSubmitting(true);
    try {
      await updatePreferences({ importanceColors: JSON.stringify(colorValues) });
      toast.success(t('colorSettingsSaved', 'Color settings saved successfully'));
    } catch (error) {
      console.error("Failed to save color settings:", error);
      toast.error(t('failedToSaveSettings', 'Failed to save settings'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="space-y-4">
        {[1, 2, 3, 4, 5].map((importance) => (
          <div key={importance} className="space-y-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <h3 className="text-lg font-medium">{importanceLabels[importance as keyof typeof importanceLabels]}</h3>
                {isDefaultColor(importance) && (
                  <Badge variant="outline" className="text-xs">Default</Badge>
                )}
              </div>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => handleReset(importance)}
              >
                {t('reset')}
              </Button>
            </div>
            
            <div className="flex items-center gap-4">
              <Badge 
                className="min-w-20 justify-center"
                style={{
                  backgroundColor: extractColorFromClass(colorValues[importance]?.bg),
                  color: extractColorFromClass(colorValues[importance]?.text)
                }}
              >
                {importanceLabels[importance as keyof typeof importanceLabels]}
              </Badge>
              
              <div className="grid grid-cols-2 gap-4 flex-1">
                <div>
                  <Label htmlFor={`bg-color-${importance}`}>Background Color</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <Input 
                      id={`bg-color-${importance}`}
                      type="color"
                      value={extractColorFromClass(colorValues[importance]?.bg)}
                      onChange={(e) => handleColorChange(importance, 'bg', e.target.value)}
                      className="w-12 h-8 p-1"
                    />
                    <Input 
                      value={extractColorFromClass(colorValues[importance]?.bg)}
                      disabled
                      className="flex-1"
                    />
                  </div>
                </div>
                
                <div>
                  <Label htmlFor={`text-color-${importance}`}>Text Color</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <Input 
                      id={`text-color-${importance}`}
                      type="color"
                      value={extractColorFromClass(colorValues[importance]?.text)}
                      onChange={(e) => handleColorChange(importance, 'text', e.target.value)}
                      className="w-12 h-8 p-1"
                    />
                    <Input 
                      value={extractColorFromClass(colorValues[importance]?.text)}
                      disabled
                      className="flex-1"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
      
      <div className="flex justify-between pt-4">
        <Button 
          variant="outline" 
          onClick={handleResetAll}
        >
          {t('resetAll')}
        </Button>
        <Button 
          onClick={handleSave}
          disabled={isSubmitting}
        >
          {isSubmitting ? t('saving') : t('saveColors')}
        </Button>
      </div>
    </div>
  );
}
